import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { get, head, isEmpty } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getAllAccounts, setAccountSession } from "actions/accountActions";
import { userConstants } from "actions/types";
import { getRequiredUpdates } from "actions/userActions";
import IconSpinner from "components/IconSpinner";
import IndividualInvestmentProfile from "pages/signUp/individual/IndividualInvestmentProfile";
import IndividualPortfolioSelection from "pages/signUp/individual/IndividualPortfolioSelection";
import CustodianDisclosure from "./CustodianDisclosure";
import { IndividualRequiredUpdatesPending } from "statics/states";
import RequiredUpdatesContributionSelection from "pages/signUp/individual/setupContribution/RequiredUpdatesContributionSelection";
import { getIraAccountIdSelector } from "store/selectors/user";
import type { RootState } from "types/state";

interface RequiredUpdate {
  entityIdentifier: {
    id: string;
    type: string;
  };
  pendingSteps?: string[];
  accountType?: string;
}

const actions = [userConstants.GET_USER_REQUIRED_STEPS];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  pendingRequiredUpdates: get(
    state,
    "user.requiredUpdates.updates",
    []
  ) as RequiredUpdate[],
  userState: get(state, "user.userState.state", "") as string,
  error: errorSelector(state),
  isFetching: loadingSelector(state),
  // selectors/user is typed: returns string | null | undefined
  accountId: getIraAccountIdSelector(state),
});

const mapDispatchToProps = {
  getRequiredUpdates,
  getAllAccounts,
  setAccountSession,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  loading: boolean;
}

// The dynamic step→component lookup renders any of four connected components
// with the same two props; their individual prop types diverge, so the map is
// typed to the common rendered-prop shape.
type ActiveComponentType = React.ComponentType<{
  updatingAccountId: string | null;
  accountType: string | null | undefined;
}>;

class IndividualRequiredUpdates extends React.Component<Props, State> {
  state: State = {
    loading: true,
  };

  getActiveUpdate(updates: RequiredUpdate[]): RequiredUpdate | undefined {
    const { accountId } = this.props;
    return (
      updates.find((u) => u.entityIdentifier.id === accountId) || head(updates)
    );
  }

  async componentDidMount() {
    await this.props.getAllAccounts(this.props.client as ApolloClient<object>);
    void this.props
      .getRequiredUpdates(this.props.client as ApolloClient<object>)
      .then(async (result) => {
        if (this.props.userState !== IndividualRequiredUpdatesPending) {
          this.props.push("/dashboard");
          return;
        }
        const pendingRequiredUpdates = get(
          result,
          "data.updates",
          []
        ) as RequiredUpdate[];
        const firstUpdate = this.getActiveUpdate(pendingRequiredUpdates);

        if (
          // PRESERVED BUG (conversion-only): firstUpdate may be undefined (head
          // of empty array); original dereferences it directly and would throw.
          firstUpdate!.entityIdentifier.type === "BankAccount" &&
          this.props.accountId !== firstUpdate!.entityIdentifier.id
        ) {
          await this.props.setAccountSession(
            this.props.client as ApolloClient<object>,
            firstUpdate!.entityIdentifier.id
          );
          window.location.reload();
        }

        this.setState({ loading: false });
      });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.loading) return;

    const { pendingRequiredUpdates = [] } = this.props;
    const prevUpdates = prevProps.pendingRequiredUpdates || [];

    if (pendingRequiredUpdates === prevUpdates) return;

    // Only redirect when going from non-empty → empty, not on initial population
    if (pendingRequiredUpdates.length === 0 && prevUpdates.length > 0) {
      this.props.push("/dashboard");
      return;
    }

    const currentFirst = this.getActiveUpdate(pendingRequiredUpdates);
    const prevFirst = this.getActiveUpdate(prevUpdates);

    if (
      currentFirst &&
      currentFirst.entityIdentifier.type === "BankAccount" &&
      currentFirst.entityIdentifier.id !== prevFirst?.entityIdentifier?.id &&
      currentFirst.entityIdentifier.id !== this.props.accountId &&
      !isEmpty(this.props.accountId)
    ) {
      void this.props
        .setAccountSession(
          this.props.client as ApolloClient<object>,
          currentFirst.entityIdentifier.id
        )
        .then(() => window.location.reload());
    }
  }

  render() {
    const { loading } = this.state;
    const { pendingRequiredUpdates = [] } = this.props;

    if (loading) {
      return <IconSpinner centered />;
    }

    const currentUpdate = this.getActiveUpdate(pendingRequiredUpdates);

    if (!currentUpdate) {
      return <div />;
    }

    const firstPendingStep = head(currentUpdate.pendingSteps);

    // The four connected components have divergent prop types and don't
    // structurally overlap with the common rendered-prop shape, so each is
    // cast through `unknown` to the shared ActiveComponentType for dispatch.
    const components: Record<string, ActiveComponentType> = {
      INVESTMENT_PROFILE_AND_SUITABILITY:
        IndividualInvestmentProfile as unknown as ActiveComponentType,
      PORTFOLIO_UPDATE:
        IndividualPortfolioSelection as unknown as ActiveComponentType,
      CUSTODIAN_DISCLOSURE_QUESTION:
        CustodianDisclosure as unknown as ActiveComponentType,
      PAYROLL_CONTRIBUTION_UPDATE:
        RequiredUpdatesContributionSelection as unknown as ActiveComponentType,
    };

    const ActiveComponent = get(components, firstPendingStep as string);

    return (
      <>
        {!isEmpty(ActiveComponent) ? (
          <ActiveComponent
            updatingAccountId={
              currentUpdate.entityIdentifier.type === "BankAccount"
                ? currentUpdate.entityIdentifier.id
                : null
            }
            accountType={
              currentUpdate.entityIdentifier.type === "BankAccount"
                ? currentUpdate.accountType
                : null
            }
          />
        ) : (
          <div />
        )}
      </>
    );
  }
}

export default connector(withApollo<Props>(IndividualRequiredUpdates));
